import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faTruck, faClock, faPoundSign
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/Route-Optimisation.png";
import video from "../../assets/videos/Van-Simulation.mp4";

const RouteOptimisation = () => {
    return (
        <>
            <SEO
                title="Route Optimisation: AI Digital Twin for Fleet Management | Nebulyx AI"
                description="Optimise delivery routes and fleet utilisation using AI Digital Twins. Reduce mileage by 29%, cut fuel costs, and eliminate under-utilised vehicles through graph-based route analysis."
                keywords="AI digital twin, route optimisation, fleet management, delivery routing, vehicle utilisation, graph analysis"
                canonicalUrl="https://nebulyx.ai/use-cases/route-optimisation"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faTruck}/>
                                            &nbsp;
                                            Transport & Logistics
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How do we reduce fleet costs without adding vehicles?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Consolidate delivery routes, eliminate under-utilised vehicles, and cut fuel costs through AI-powered graph analysis of your fleet operations.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">29%</span>
                                <span className="stat-label">Mileage Reduction</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faPoundSign} className="stat-icon"/>
                                <span className="stat-value">£150k</span>
                                <span className="stat-label">Annual Savings</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">5 Vehicles</span>
                                <span className="stat-label">Freed for Redeployment</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Vehicles running under capacity on daily routes</li>
                                            <li>Overlapping delivery zones creating redundant mileage</li>
                                            <li>Manual route planning can't optimise across fleet</li>
                                            <li>Rising fuel costs eroding delivery margins</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Built Data Object Graph of fleet operations</li>
                                            <li>Connected depots, customers, vehicles, and routes</li>
                                            <li>AI-powered route consolidation engine</li>
                                            <li>Real-time capacity utilisation analysis</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Route optimisation in minutes not hours</li>
                                            <li>12,240 miles saved annually</li>
                                            <li>34% under-utilisation eliminated</li>
                                            <li>347kg CO₂ reduction per month</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Ingests telematics, delivery manifests, and depot data to build a Data Object Graph that identifies consolidation opportunities and outputs optimised multi-drop routes.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Sources</strong>
                                    <span>Vehicle telematics, delivery manifests, customer locations, depot inventory</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Build Graph</strong>
                                    <span>Data Object Graph maps routes, capacity, time windows, and geographic clusters</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Run Scenarios</strong>
                                    <span>What-if engine tests route consolidation, resequencing, and depot shifts</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Optimise Output</strong>
                                    <span>Recommended routes, vehicle assignments, and driver schedules per day</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests data from vehicle telematics, delivery management systems, and customer databases to model
                                            fleet operations end-to-end. Graph traversal identifies under-utilised vehicles and calculates
                                            optimal consolidation strategies that maintain service levels while cutting costs.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Route Optimisation Dashboard</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Displays live fleet positions, capacity utilisation by vehicle, and AI-recommended
                                            consolidation actions. Allows planners to simulate route changes and see mileage,
                                            fuel, and cost impacts before committing to the optimised plan.</p>
                                        <div className="image-parent">
                                            <video autoPlay loop src={video} className="w-100 h-100"/>
                                            {/*<img className={"image-child-span w-100 h-100"} src={vis} alt=""/>*/}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                                    {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Vehicle Telematics (GPS, speed)</li>
                                            <li>Delivery Manifests</li>
                                            <li>Customer Time Windows</li>
                                            <li>Fuel Transaction Records</li>
                                            <li>Driver Roster & Hours</li>
                                            <li>Depot Inventory Levels</li>
                                            <li>Live Traffic Feed</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Graph-based route optimisation</li>
                                            <li>Capacity utilisation analysis</li>
                                            <li>Traffic pattern prediction</li>
                                            <li>Fuel consumption regression</li>
                                            <li>Time window constraint solving</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Consolidate under-loaded vehicles</li>
                                            <li>Re-sequence delivery order</li>
                                            <li>Shift depot location</li>
                                            <li>Add 10% more vehicles</li>
                                            <li>Narrow delivery windows</li>
                                            <li>Transition 20% fleet to EV</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling the fleet as a <strong>Data Object Graph</strong>, we see that
                                        consolidating deliveries into optimised multi-drop routes-rather than adding vehicles-
                                        <strong> can reduce fuel consumption by 29% and cuts fleet mileage by 12,000 miles monthly</strong>.
                                        Graph traversal reveals 34% of vehicles were running under 60% capacity, an inefficiency
                                        invisible to traditional route planning tools.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Optimal Strategy Found</span>
                                        <span className="discovery-value">Route Consolidation</span>
                                        <span className="discovery-note">5 vehicles freed, 29% miles saved</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Struggling with Fleet Efficiency?</h2>
                            <p>
                                If your delivery fleet has hidden capacity and rising fuel costs are eating into margins,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default RouteOptimisation;